import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ArrowLeft, Users, Search, MapPin, Loader2, UserPlus,
  MessageSquare, Award, Globe, ExternalLink, Star, Verified,
  Building2,
} from "lucide-react";
import GeoFilterBar from "@/components/GeoFilterBar";
import TopNav from "@/components/TopNav";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const dummyAvatar = (name, userId) => {
  const gradients = [
    "from-emerald-500 to-teal-600", "from-blue-500 to-cyan-600",
    "from-violet-500 to-purple-600", "from-rose-500 to-pink-600",
    "from-amber-500 to-orange-600",
  ];
  const hash = (userId || name || "").length;
  return { bg: gradients[hash % gradients.length], letter: (name || "?")[0].toUpperCase() };
};

export default function PeopleDirectory() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [search, setSearch] = useState("");
  const [location, setLocation] = useState("");
  const [skill, setSkill] = useState("");
  const [filterCountry, setFilterCountry] = useState("all");
  const [filterRegion, setFilterRegion] = useState("all");
  const [radiusKm, setRadiusKm] = useState("");
  const [radius, setRadius] = useState("50");
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [authUser, setAuthUser] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem("token");
    if (stored) {
      try {
        const payload = JSON.parse(atob(stored.split(".")[1]));
        setAuthUser(payload);
      } catch {}
    }
  }, []);

  const fetchPeople = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/v2/people`);
      let items = res.data.items || [];
      if (search) {
        const q = search.toLowerCase();
        items = items.filter(p => (p.name || "").toLowerCase().includes(q) || (p.username || "").toLowerCase().includes(q));
      }
      if (location) items = items.filter(p => (p.country || "").toLowerCase().includes(location.toLowerCase()));
      if (skill) items = items.filter(p => {
        const s = Array.isArray(p.skills) ? p.skills.join(" ") : (p.skills || "");
        return s.toLowerCase().includes(skill.toLowerCase());
      });
      setPeople(items);
      setTotal(items.length);
    } catch { setPeople([]); }
    setLoading(false);
  }, [search, location, skill]);

  useEffect(() => { fetchPeople(); }, [fetchPeople]);

  const handleFollow = async (uid) => {
    try {
      await axios.post(`${API}/people/${uid}/follow`, { tier: "free" }, { headers: getHeaders() });
      toast.success("Following user!");
    } catch { toast.error("Please login first"); }
  };

  const handleChatInvite = async (uid) => {
    try {
      await axios.post(`${API}/send-chat-invite`, { to_user_id: uid }, { headers: getHeaders() });
      toast.success("Chat invitation sent!");
    } catch { toast.error("Please login first"); }
  };

  return (
    <div className="min-h-screen bg-app text-app">
      <TopNav />
      <header className="border-b border-emerald-800/30 bg-emerald-950/60 backdrop-blur-xl sticky top-0 z-50 px-6 py-4 flex items-center gap-4">
        <button onClick={() => navigate("/dashboard")} className="text-zinc-400 hover:text-white p-1">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
          <Users className="w-5 h-5 text-white" />
        </div>
        <span className="font-bold text-lg">People Hub</span>
        <Badge className="bg-emerald-600/30 text-emerald-200 border-emerald-600/40 text-xs">{total}</Badge>
      </header>
      <div className="max-w-7xl mx-auto px-6 pt-4">
        {/* Corporate registration CTA */}
        <div className="mb-4 p-4 bg-gradient-to-r from-emerald-900/60 to-teal-900/40 border border-emerald-600/40 rounded-xl">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white shrink-0">
              <Building2 className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-emerald-200">Register Your Corporate Account</h4>
              <p className="text-xs text-violet-300/70 mt-1">
                Get your organisation on the map! Corporate accounts unlock: Verified status, Post Deals & Offers, Access People Directories,
                Create Group Buys, Use Corporate Tools, and earn <strong className="text-amber-300">1,000 FREE ALLs</strong> on signup.
              </p>
              <button onClick={() => navigate("/?register=corporate")}
                className="mt-2 bg-violet-600 hover:bg-violet-500 text-white text-xs font-medium px-4 py-1.5 rounded-lg transition-colors">
                Register Now
              </button>
            </div>
          </div>
        </div>

        <GeoFilterBar country={filterCountry} onCountryChange={setFilterCountry} region={filterRegion} onRegionChange={setFilterRegion} radius={radiusKm} onRadiusChange={setRadiusKm} />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input placeholder="Search by name or username..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 bg-emerald-950/40 border border-emerald-800/40 text-white rounded-full text-sm h-9 placeholder:text-emerald-300/60 focus:outline-none focus:border-emerald-400" />
          </div>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input placeholder="Location (country)" value={location} onChange={e => setLocation(e.target.value)}
              className="w-full pl-9 bg-emerald-950/40 border border-emerald-800/40 text-white rounded-full text-sm h-9 placeholder:text-emerald-300/60 focus:outline-none focus:border-emerald-400" />
          </div>
          <div className="relative">
            <Award className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input placeholder="Skill / Expertise" value={skill} onChange={e => setSkill(e.target.value)}
              className="w-full pl-9 bg-emerald-950/40 border border-emerald-800/40 text-white rounded-full text-sm h-9 placeholder:text-emerald-300/60 focus:outline-none focus:border-emerald-400" />
          </div>
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-zinc-500 shrink-0" />
            <input type="number" placeholder="Radius" value={radius} onChange={e => setRadius(e.target.value)}
              className="w-full bg-emerald-950/40 border border-emerald-800/40 text-white rounded-full text-sm h-9 text-center placeholder:text-emerald-300/60 focus:outline-none focus:border-emerald-400" min={1} max={500} />
            <span className="text-xs text-zinc-500">km</span>
          </div>
        </div>
      </div>
      <main className="max-w-7xl mx-auto px-6 pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
        ) : people.length === 0 ? (
          <div className="text-center py-20 text-zinc-500">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-lg">No people found</p>
            <p className="text-sm text-zinc-600">Try adjusting your search filters</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {people.map(person => {
                const avatar = dummyAvatar(person.name, person.user_id);
                return (
                  <Card key={person.user_id}
                    className="bg-app-card border-white/10 p-0 hover:bg-emerald-900/30 transition-all hover:border-emerald-400/50 overflow-hidden group cursor-pointer"
                    onClick={() => navigate(`/people/${person.user_id}`)}>
                    <div className="p-4 pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${avatar.bg} flex items-center justify-center text-white font-bold text-lg shrink-0 shadow-lg`}>
                            {avatar.letter}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="font-semibold text-sm">{person.name}</p>
                              {person.verified && <Star className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                            </div>
                            <p className="text-[11px] text-zinc-500">@{person.username || "user"}</p>
                            {person.position && <p className="text-[10px] text-zinc-600">{person.position}</p>}
                          </div>
                        </div>
                        {person.user_id !== authUser?.user_id && (
                          <button onClick={(e) => { e.stopPropagation(); handleFollow(person.user_id); }}
                            className="text-zinc-500 hover:text-emerald-400 transition-colors">
                            <UserPlus className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    {person.skills && Array.isArray(person.skills) && person.skills.length > 0 && (
                      <div className="px-4 flex flex-wrap gap-1 mb-1">
                        {person.skills.slice(0, 3).map((s, i) => (
                          <Badge key={i} className="bg-emerald-600/10 text-emerald-300 border-emerald-600/20 text-[10px]">
                            {s}
                          </Badge>
                        ))}
                        {person.skills.length > 3 && <span className="text-[10px] text-zinc-500 self-center">+{person.skills.length - 3}</span>}
                      </div>
                    )}
                    {person.services && Array.isArray(person.services) && person.services.length > 0 && (
                      <div className="px-4 flex flex-wrap gap-1 mb-1">
                        {person.services.slice(0, 2).map((s, i) => (
                          <Badge key={i} className="bg-amber-600/10 text-amber-300 border-amber-600/20 text-[8px]">{s}</Badge>
                        ))}
                      </div>
                    )}
                    <div className="px-4 pb-3">
                      <div className="flex items-center gap-2 text-[11px] text-zinc-500 mb-2">
                        {person.country && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{person.country}</span>}
                      </div>
                      {person.user_id !== authUser?.user_id && (
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={(e) => { e.stopPropagation(); handleChatInvite(person.user_id); }}
                            className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300">
                            <MessageSquare className="w-3 h-3" /> Chat
                          </button>
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
            <p className="text-center text-xs text-zinc-600 mt-6">{total} members</p>
          </>
        )}
      </main>
    </div>
  );
}
